export const ZOOM_SCALES = [0.12, 0.22, 0.38, 0.58, 0.85] as const;

export interface CropRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface CropMeta extends CropRect {
  scale?: number;
  focalX?: number;
  focalY?: number;
}

export function clampRect(rect: CropRect, imgW: number, imgH: number): CropRect {
  let { left, top, width, height } = rect;
  width = Math.max(32, Math.min(Math.floor(width), imgW));
  height = Math.max(32, Math.min(Math.floor(height), imgH));
  left = Math.max(0, Math.min(Math.floor(left), imgW - width));
  top = Math.max(0, Math.min(Math.floor(top), imgH - height));
  return { left, top, width, height };
}

export function computeAutoCropRect(
  level: number,
  imgW: number,
  imgH: number,
  focalX = 0.5,
  focalY = 0.5,
): CropRect & { scale: number } {
  const scale = ZOOM_SCALES[level] ?? ZOOM_SCALES[0];
  const cropW = Math.max(32, Math.floor(imgW * scale));
  const cropH = Math.max(32, Math.floor(imgH * scale));
  const cx = Math.floor(imgW * focalX);
  const cy = Math.floor(imgH * focalY);
  const left = Math.max(0, Math.min(imgW - cropW, cx - Math.floor(cropW / 2)));
  const top = Math.max(0, Math.min(imgH - cropH, cy - Math.floor(cropH / 2)));
  return { left, top, width: cropW, height: cropH, scale };
}

export function rectToFocal(rect: CropRect, imgW: number, imgH: number) {
  return {
    focalX: (rect.left + rect.width / 2) / imgW,
    focalY: (rect.top + rect.height / 2) / imgH,
  };
}

export type ResizeCorner = 'nw' | 'ne' | 'sw' | 'se';

function clampWithAnchorNW(
  left: number,
  top: number,
  width: number,
  height: number,
  imgW: number,
  imgH: number,
): CropRect {
  const MIN = 32;
  left = Math.max(0, Math.min(Math.floor(left), imgW - MIN));
  top = Math.max(0, Math.min(Math.floor(top), imgH - MIN));
  width = Math.max(MIN, Math.min(Math.floor(width), imgW - left));
  height = Math.max(MIN, Math.min(Math.floor(height), imgH - top));
  return { left, top, width, height };
}

function clampWithAnchorSE(
  right: number,
  bottom: number,
  width: number,
  height: number,
  imgW: number,
  imgH: number,
): CropRect {
  const MIN = 32;
  right = Math.max(MIN, Math.min(Math.floor(right), imgW));
  bottom = Math.max(MIN, Math.min(Math.floor(bottom), imgH));
  width = Math.max(MIN, Math.min(Math.floor(width), right));
  height = Math.max(MIN, Math.min(Math.floor(height), bottom));
  let left = right - width;
  let top = bottom - height;
  if (left < 0) {
    left = 0;
    width = right - left;
  }
  if (top < 0) {
    top = 0;
    height = bottom - top;
  }
  return { left, top, width, height };
}

function clampWithAnchorNE(
  right: number,
  top: number,
  width: number,
  height: number,
  imgW: number,
  imgH: number,
): CropRect {
  const MIN = 32;
  right = Math.max(MIN, Math.min(Math.floor(right), imgW));
  top = Math.max(0, Math.min(Math.floor(top), imgH - MIN));
  width = Math.max(MIN, Math.min(Math.floor(width), right));
  height = Math.max(MIN, Math.min(Math.floor(height), imgH - top));
  let left = right - width;
  if (left < 0) {
    left = 0;
    width = right - left;
  }
  return { left, top, width, height };
}

function clampWithAnchorSW(
  left: number,
  bottom: number,
  width: number,
  height: number,
  imgW: number,
  imgH: number,
): CropRect {
  const MIN = 32;
  left = Math.max(0, Math.min(Math.floor(left), imgW - MIN));
  bottom = Math.max(MIN, Math.min(Math.floor(bottom), imgH));
  width = Math.max(MIN, Math.min(Math.floor(width), imgW - left));
  height = Math.max(MIN, Math.min(Math.floor(height), bottom));
  let top = bottom - height;
  if (top < 0) {
    top = 0;
    height = bottom - top;
  }
  return { left, top, width, height };
}

/** Redimensionne depuis un coin ; le coin opposé reste fixe. */
export function resizeRectFromCorner(
  start: CropRect,
  corner: ResizeCorner,
  dx: number,
  dy: number,
  imgW: number,
  imgH: number,
): CropRect {
  const right = start.left + start.width;
  const bottom = start.top + start.height;

  switch (corner) {
    case 'se':
      return clampWithAnchorNW(
        start.left,
        start.top,
        start.width + dx,
        start.height + dy,
        imgW,
        imgH,
      );
    case 'nw':
      return clampWithAnchorSE(
        right,
        bottom,
        start.width - dx,
        start.height - dy,
        imgW,
        imgH,
      );
    case 'ne':
      return clampWithAnchorSW(
        start.left,
        bottom,
        start.width + dx,
        start.height - dy,
        imgW,
        imgH,
      );
    case 'sw':
      return clampWithAnchorNE(
        right,
        start.top,
        start.width - dx,
        start.height + dy,
        imgW,
        imgH,
      );
  }
}

export function resizeRectFieldAnchored(
  rect: CropRect,
  field: 'width' | 'height',
  value: number,
  imgW: number,
  imgH: number,
): CropRect {
  const width = field === 'width' ? value : rect.width;
  const height = field === 'height' ? value : rect.height;
  return clampWithAnchorNW(rect.left, rect.top, width, height, imgW, imgH);
}

export function buildAllLevelRects(
  imgW: number,
  imgH: number,
  focalX: number,
  focalY: number,
  custom: Partial<Record<number, CropRect>>,
): Record<number, CropRect> {
  const out: Record<number, CropRect> = {};
  for (let level = 0; level < 5; level++) {
    out[level] = custom[level]
      ? clampRect(custom[level]!, imgW, imgH)
      : computeAutoCropRect(level, imgW, imgH, focalX, focalY);
  }
  return out;
}

export function rectToPayload(levels: Record<number, CropRect>) {
  return Object.entries(levels).map(([level, rect]) => ({
    level: Number(level),
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  }));
}
